import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { paths } from 'config'
import { useSessionExpiresAt } from 'shared/components/SessionExpiresAtContext'
import {
  getSessionExpiryHeldAtMostUntil,
  holdSessionExpiry,
  runWhenTimeReached,
  useIsExtendingSession,
} from 'shared/utils'
import { useAuthParams } from './use-auth-params'
import { useIsSessionExpiryHeldByAnotherTab } from './use-is-session-expiry-held-by-another-tab'

const REAUTHENTICATION_PATHS: string[] = [paths.signIn, paths.signInWithLink]

const useIsReauthenticationScreen = () => {
  const { pathname } = useLocation()
  const { reauthenticate } = useAuthParams()

  return reauthenticate && REAUTHENTICATION_PATHS.includes(pathname)
}

// False once the time is reached, and without a time
const useIsBeforeTime = (time?: number) => {
  const [isBefore, setIsBefore] = useState(
    () => time !== undefined && Date.now() < time
  )

  useEffect(() => {
    if (time === undefined) {
      setIsBefore(false)
      return
    }

    setIsBefore(Date.now() < time)
    return runWhenTimeReached(time, () => setIsBefore(false))
  }, [time])

  return isBefore
}

// Signing out on the reauthentication screen would make the reauthentication
// fail, and a successful one starts a new session. The screen shows no data,
// so an expired session is ended only once the user leaves it. The session
// extension dialog shows the page behind it, so it keeps the session only for a
// limited time after it expires, and leaving it, also by reloading or closing
// the tab, ends an expired session at once. Signing out in one tab signs out
// all of them, so the other tabs wait for the reauthentication as well, also
// only for a limited time.
// Reads the route, so it has to be used inside the router.
export const useIsSessionExpiryPaused = () => {
  const isReauthenticationScreen = useIsReauthenticationScreen()
  const isExtendingSession = useIsExtendingSession()
  const sessionExpiresAt = useSessionExpiresAt()
  const heldAtMostUntil = getSessionExpiryHeldAtMostUntil(sessionExpiresAt)
  const isHeldByAnotherTab = useIsSessionExpiryHeldByAnotherTab(heldAtMostUntil)
  const isWithinHoldLimit = useIsBeforeTime(heldAtMostUntil)

  useEffect(() => {
    if (!isReauthenticationScreen) return
    return holdSessionExpiry()
  }, [isReauthenticationScreen])

  // Effects aren't cleaned up when the page is left, and the other tabs would
  // wait for a stale hold until it runs out. The reloaded tab would too.
  useEffect(() => {
    if (!isExtendingSession) return

    const stopHold = holdSessionExpiry()
    window.addEventListener('pagehide', stopHold)

    return () => {
      window.removeEventListener('pagehide', stopHold)
      stopHold()
    }
  }, [isExtendingSession])

  return (
    isReauthenticationScreen ||
    (isExtendingSession && isWithinHoldLimit) ||
    isHeldByAnotherTab
  )
}
